/**
 * @file 学習者の直近の取り組みの記録を、新しい順にまとめて読む hook。
 *
 * @what: 記録を新しい順に読み (1回100件、最大 SCAN_LIMIT 件まで)、そのままの配列で返す。本だなの
 *   「前回の記録」(hooks/useLastSourceLogs.ts) と「学んだ日」(hooks/useStudyDays.ts) が同じ配列を使う。
 * @why: 資料への記録は detail の JSON で紐づくので、サーバで資料ごとにも期間でも引けない (暗号化
 *   ペイロードの中)。本だなの面で必要なのは直近だけなので、読む範囲を直近に限り、読み取りは面で1回にする。
 * @failure: 途中で失敗したら、そこまでに読めたぶんを返す (詳細は console)。まなぶ人が未定なら空。
 */

import { useEffect, useState } from "react";
import { fetchProgressLogs, type StudyProgressLog } from "../api/plans";

/** 読み進める記録の上限。毎日1件残して半年ぶんを超える (ReadingPanel と同じ規模)。 */
export const RECENT_LOGS_SCAN_LIMIT = 200;
const PAGE_LIMIT = 100;

const EMPTY: readonly StudyProgressLog[] = [];

export function useRecentProgressLogs(
  familyId: string,
  personId: string | null,
): readonly StudyProgressLog[] {
  const [logs, setLogs] = useState<readonly StudyProgressLog[]>(EMPTY);

  useEffect(() => {
    setLogs(EMPTY);
    if (!personId) return;
    const controller = new AbortController();
    (async () => {
      const got: StudyProgressLog[] = [];
      let cursor: string | undefined;
      try {
        while (got.length < RECENT_LOGS_SCAN_LIMIT) {
          const page = await fetchProgressLogs(
            familyId,
            personId,
            cursor,
            controller.signal,
            PAGE_LIMIT,
          );
          got.push(...page.items);
          if (!page.next_cursor || page.items.length === 0) break;
          cursor = page.next_cursor;
        }
        if (!controller.signal.aborted) setLogs(got);
      } catch (err: unknown) {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load the recent progress logs", err);
        // 読めたぶんだけで組み立てる (本だなは読める)
        setLogs(got);
      }
    })();
    return () => controller.abort();
  }, [familyId, personId]);

  return logs;
}
